// ═══════════════════════════════════════════════════════════════════════════
// Bodegas y traslados
//
// El operador, al retomar el hospedaje de varias vinícolas: "Debe soportar
// múltiples bodegas. Un inventario único con la opción de mercancía en
// tránsito."
//
// ── LO QUE YA ESTABA, Y POR QUÉ NO SE VEÍA ─────────────────────────────────
//
// Casi todo. `stock_actual` agrupa por (inquilino, producto, BODEGA) desde la
// migración 0001; el número único que enseña la app sale de que `lectura.js`
// lo suma al leerlo. O sea que el detalle por bodega nunca faltó — se tiraba
// en el camino.
//
// Este módulo lo lee sin sumarlo. No sustituye a `lectura.js`: aquella sigue
// entregando el total, que es lo que necesitan Productos y la remisión. Aquí
// se pide el desglose solo cuando alguien abre la pantalla de Bodegas, que es
// cuando importa.
//
// ── POR QUÉ NO PASA POR LA BITÁCORA ────────────────────────────────────────
//
// El catálogo se encola y sube cuando hay red (`sincronizador.js`). Un
// traslado NO: mueve existencias, y dos dispositivos encolando traslados sin
// verse producirían el mismo desorden que produciría inventar folios sin
// conexión. Se hace contra el servidor o no se hace.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";

/** Las bodegas del inquilino, con su tipo. */
export async function leerBodegas(tenantId) {
  const { data, error } = await supabase
    .from("warehouse")
    .select("id, name, type, is_active, is_outbound, sede")
    .eq("tenant_id", tenantId)
    .is("deleted_at", null)
    .order("name");
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Existencias POR BODEGA, indexadas por bodega y por producto.
 *
 * Devuelve { porBodega: Map(bodegaId → Map(productoPg → qty)), total: Map(...) }
 * con el producto en su id de Postgres — quien llama lo cruza con el catálogo.
 */
export async function leerExistenciasPorBodega(tenantId) {
  const { data, error } = await supabase
    .from("stock_actual")
    .select("product_id, warehouse_id, qty")
    .eq("tenant_id", tenantId);
  if (error) throw new Error(error.message);

  const porBodega = new Map();
  const total = new Map();
  for (const f of data || []) {
    const n = Number(f.qty) || 0;
    if (!porBodega.has(f.warehouse_id)) porBodega.set(f.warehouse_id, new Map());
    porBodega.get(f.warehouse_id).set(f.product_id, n);
    total.set(f.product_id, (total.get(f.product_id) || 0) + n);
  }
  return { porBodega, total };
}

/** Crea o edita una bodega. `id` null = nueva. */
export async function guardarBodega(tenantId, { id, nombre, tipo, esSalida, activa, sede }) {
  const { data, error } = await supabase.rpc("guardar_bodega", {
    p_tenant: tenantId,
    p_id: id || null,
    p_nombre: nombre,
    p_tipo: tipo,
    p_is_outbound: !!esSalida,
    p_activa: activa !== false,
    p_sede: (sede || "").trim() || null,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Mueve existencias de una bodega a otra.
 *
 * Devuelve lo que contesta el servidor, INCLUIDO su `aviso`: cuando el origen
 * queda en negativo el traslado se hace igual y avisa, por la misma razón que
 * la sobreventa no se bloquea — lo que falta son entradas sin registrar, no un
 * movimiento inventado. Quien llame tiene que enseñar ese aviso: tragárselo
 * sería repetir lo de `ya_existia`, que se devolvía y nadie miraba.
 */
export async function trasladar(tenantId, { productoLegacy, origen, destino, cantidad, nota }) {
  const { data, error } = await supabase.rpc("trasladar_inventario", {
    p_tenant: tenantId,
    p_product: productoLegacy,
    p_origen: origen,
    p_destino: destino,
    p_qty: cantidad,
    p_nota: nota || null,
    p_device: (() => { try { return localStorage.getItem("vt-device-id"); } catch { return null; } })(),
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * ¿Ese movimiento involucra transporte?
 *
 * Sedes distintas, sí. La misma, no. Y si alguna de las dos no dice dónde
 * está, la respuesta es `null` — «no se sabe», que no es lo mismo que «no».
 * Afirmar que no hay transporte sobre un camión de verdad es peor que callar,
 * así que la pantalla enseña «sin declarar» en vez de un «no» inventado.
 */
export function hayTransporte(origen, destino) {
  // Se recorta antes de comparar. `guardar_bodega` ya convierte "" en null,
  // pero una sede de puros espacios se colaba: `"   "` es verdadero en
  // JavaScript, así que dos bodegas «sin sede» se leían como si estuvieran en
  // el mismo domicilio y la respuesta era «no hay transporte». Lo encontró la
  // prueba, no la lectura.
  const a = (origen?.sede || "").trim();
  const b = (destino?.sede || "").trim();
  if (!a || !b) return null;
  return a !== b;
}

/** Los traslados recientes, ya emparejados por `transfer_id`. */
export async function leerTraslados(tenantId, limite = 40) {
  const { data, error } = await supabase
    .from("inventory_movement")
    .select("transfer_id, product_id, warehouse_id, qty, occurred_at, note")
    .eq("tenant_id", tenantId)
    .not("transfer_id", "is", null)
    .order("occurred_at", { ascending: false })
    .limit(limite * 2);           // dos asientos por traslado
  if (error) throw new Error(error.message);
  return emparejarTraslados(data || []);
}

/**
 * Junta los asientos de cada traslado en un renglón legible.
 *
 * Se exporta para poder probarlo: el emparejamiento es donde cabe el error
 * —un traslado a medias, dos con el mismo id— y depender de la red para
 * comprobarlo sería no comprobarlo.
 */
export function emparejarTraslados(movimientos) {
  const porId = new Map();
  for (const m of movimientos) {
    if (!porId.has(m.transfer_id)) porId.set(m.transfer_id, []);
    porId.get(m.transfer_id).push(m);
  }
  const salida = [];
  for (const [id, asientos] of porId) {
    const sale = asientos.find(a => Number(a.qty) < 0);
    const entra = asientos.find(a => Number(a.qty) > 0);
    // Un traslado al que le falta una mitad NO se enseña como si estuviera
    // completo: se marca. `verificar_traslados()` comprueba que no existan,
    // pero si alguna vez existe, mentir aquí lo escondería.
    salida.push({
      id,
      productoPg: (entra || sale)?.product_id,
      origen: sale?.warehouse_id || null,
      destino: entra?.warehouse_id || null,
      cantidad: entra ? Number(entra.qty) : Math.abs(Number(sale?.qty) || 0),
      fecha: (entra || sale)?.occurred_at,
      nota: (entra || sale)?.note || "",
      completo: !!sale && !!entra && asientos.length === 2,
    });
  }
  return salida.sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)));
}

// ═══════════════════════════════════════════════════════════════════════════
//  De qué bodega sale una remisión
// ═══════════════════════════════════════════════════════════════════════════

const CLAVE_ULTIMA = "vt-bodega-de-salida";

/**
 * Las bodegas desde las que se puede despachar.
 *
 * La regla —activas, que no sean de tránsito, las de salida primero— vive en
 * la base (`bodegas_de_salida`), que es la misma que usan los respaldos de
 * `crear_remision`. Repetirla aquí las dejaría separarse: la pantalla
 * ofrecería una bodega desde la que el servidor no despacha, o al revés.
 */
export async function leerBodegasDeSalida(tenantId) {
  const { data, error } = await supabase.rpc("bodegas_de_salida", { p_tenant: tenantId });
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Cuál venía usando este dispositivo.
 *
 * Se recuerda por vinícola: quien despacha siempre desde la misma bodega no
 * tiene que elegirla cada vez, y quien entra a otra vinícola no hereda la de
 * la anterior. Si la guardada ya no está entre las posibles —se dio de baja,
 * se renombró— se devuelve la primera, que por el orden del servidor es una
 * marcada de salida.
 */
export function bodegaRecordada(tenantId, posibles) {
  if (!posibles?.length) return null;
  let guardada = null;
  try { guardada = JSON.parse(localStorage.getItem(CLAVE_ULTIMA) || "{}")[tenantId]; } catch { /* sin caché */ }
  return posibles.some(b => b.id === guardada) ? guardada : posibles[0].id;
}

export function recordarBodega(tenantId, bodegaId) {
  try {
    const todas = JSON.parse(localStorage.getItem(CLAVE_ULTIMA) || "{}");
    todas[tenantId] = bodegaId;
    localStorage.setItem(CLAVE_ULTIMA, JSON.stringify(todas));
  } catch { /* que no se pueda recordar no impide emitir */ }
}
